import React from 'react';

import type { TextFieldProps } from '@mui/material';
import { TextField } from '@mui/material';

/**
 * @see https://en.wikipedia.org/wiki/Control_character#In_Unicode
 */
const controlCharacterRegex = /[\u0000-\u0008\u000E-\u001F\u007F-\u009F]/g;

/**
 * NOTE: 컨트롤 문자가 들어오는 것을 제거합니다. 특히 macOS 에서 다국어 입력 시 쉽게 발생합니다.
 */
export const workaroundTextValue = (value: string) => {
  return value.replaceAll(controlCharacterRegex, '');
};

/**
 * 컨트롤 문자를 제거하고 커서를 보정합니다.
 */
export const workaroundTextOnChange = (
  ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>,
) => {
  const { selectionStart, selectionEnd } = ev.target;
  const doFixSelection =
    selectionStart != null && ev.target.value.charAt(selectionStart - 1).search(controlCharacterRegex) !== -1;

  ev.target.value = workaroundTextValue(ev.target.value);

  onChange?.(ev);

  // 백스페이스 입력으로 \b 발생 시, 커서 위치를 보정합니다.
  if (doFixSelection && selectionEnd != null) {
    ev.target.setSelectionRange(selectionStart - 1, selectionEnd - 1);
  }
};

/**
 * MUI TextField 의 workaround wrapper 입니다.
 *
 * - 백스페이스(backspace) 등의 컨트롤 문자 입력 방지.
 */
export const TextFieldWorkaround = React.forwardRef<HTMLDivElement, TextFieldProps>(function LookoTextField(
  { onChange, ...props },
  ref,
) {
  const onChangeCallback: NonNullable<TextFieldProps['onChange']> = React.useCallback(
    ev => workaroundTextOnChange(ev, onChange),
    [onChange],
  );

  return <TextField ref={ref} {...props} onChange={onChangeCallback} />;
});
